import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, DollarSign,
  PoundSterling, Banknote, AlertCircle, Loader, RefreshCw,
  Plus, ArrowRight, User, MapPin, CreditCard, Lock, FileText,
  Edit3, Building, Upload, ChevronRight
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

/* ─────────────────────────────────────────────────────────────────────────────
   BALANCE — mirrors getLoadInitalData() in index.js
   GET /api/users/{username} → responseData.usernameInfo.accountBalance
───────────────────────────────────────────────────────────────────────────── */
const fetchDashboardUser = async (username) => {
  const res  = await fetch(`${API}/users/${username}`);
  const data = await res.json();
  return data?.usernameInfo ?? null;
};

/* ─────────────────────────────────────────────────────────────────────────────
   Parse raw Stripe account value into a normalised status object.

   API response shapes:
     allAccountObj.retrieveUSD  = full Stripe account object (or integer >= 0 when none)
     allAccountObj.retrieveGBP  = 2   (no account)
     allAccountObj.retrieveYEN  = 2   (no account)

   Status hierarchy:
     NOT_CONNECTED  value is 2 or null/undefined
     APPROVED       charges_enabled && payouts_enabled
     RESTRICTED     requirements.past_due.length > 0  OR disabled_reason set
     PENDING        account exists, nothing past-due yet
───────────────────────────────────────────────────────────────────────────── */
const parseAccountStatus = (rawValue) => {
  if (rawValue === 2 || rawValue === null || rawValue === undefined || rawValue >= 0) {
    return {
      status: 'NOT_CONNECTED', label: 'Not Connected',
      canAction: true, accountData: null,
      reqs: { currentlyDue: [], pastDue: [], eventuallyDue: [] },
      individual: null, externalAccounts: 0,
    };
  }

  const acct           = rawValue;
  const req            = acct.requirements        || {};
  const pastDue        = req.past_due             || [];
  const currentlyDue   = req.currently_due        || [];
  const eventuallyDue  = req.eventually_due       || [];
  const disabledReason = req.disabled_reason      || null;
  const extAccounts    = acct.external_accounts?.total_count ?? 0;
  const individual     = acct.individual          || null;

  if (acct.charges_enabled && acct.payouts_enabled) {
    return {
      status: 'APPROVED', label: 'Approved & Active',
      canAction: false, accountData: acct,
      reqs: { currentlyDue, pastDue, eventuallyDue },
      individual, externalAccounts: extAccounts,
    };
  }

  if (pastDue.length > 0 || disabledReason) {
    return {
      status: 'RESTRICTED', label: 'Restricted – Action Needed',
      canAction: true, accountData: acct,
      reqs: { currentlyDue, pastDue, eventuallyDue },
      individual, externalAccounts: extAccounts,
    };
  }

  return {
    status: 'PENDING', label: 'Pending Review',
    canAction: true, accountData: acct,
    reqs: { currentlyDue, pastDue, eventuallyDue },
    individual, externalAccounts: extAccounts,
  };
};

/* ── friendly labels for Stripe requirement keys ── */
const REQ_LABEL = {
  'external_account':               'Bank / External Account',
  'individual.address.city':        'City',
  'individual.address.line1':       'Street Address',
  'individual.address.postal_code': 'Postal Code',
  'individual.address.state':       'State / Province',
  'individual.ssn_last_4':          'SSN Last 4 Digits',
  'individual.id_number':           'Full SSN / ID Number',
  'individual.dob.day':             'Date of Birth – Day',
  'individual.dob.month':           'Date of Birth – Month',
  'individual.dob.year':            'Date of Birth – Year',
  'individual.email':               'Email Address',
  'individual.phone':               'Phone Number',
  'individual.first_name':          'First Name',
  'individual.last_name':           'Last Name',
  'individual.verification.document': 'Identity Document',
};

const friendlyReq = (key) =>
  REQ_LABEL[key] ||
  key.split('.').map(w => w.replace(/_/g, ' ')).join(' › ')
     .replace(/\b\w/g, l => l.toUpperCase());

/* ── status → UI config ── */
const S = {
  NOT_CONNECTED: { bg: 'bg-slate-50',  border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600',  icon: AlertCircle, iconCls: 'text-slate-500',  btn: 'bg-slate-900 hover:bg-slate-700',  btnLabel: 'Connect Account' },
  APPROVED:      { bg: 'bg-green-50',  border: 'border-green-300', badge: 'bg-green-100 text-green-700',  icon: CheckCircle, iconCls: 'text-green-600',   btn: null,                               btnLabel: null },
  PENDING:       { bg: 'bg-amber-50',  border: 'border-amber-300', badge: 'bg-amber-100 text-amber-700',  icon: Clock,       iconCls: 'text-amber-600',   btn: 'bg-amber-500 hover:bg-amber-600',  btnLabel: 'Continue Setup' },
  RESTRICTED:    { bg: 'bg-red-50',    border: 'border-red-300',   badge: 'bg-red-100 text-red-700',      icon: XCircle,     iconCls: 'text-red-600',     btn: 'bg-red-600 hover:bg-red-700',      btnLabel: 'Resolve & Continue' },
};

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function AccountVerification() {
  const navigate                               = useNavigate();
  const { user: ctxUser, balance, updateBalance, refreshUserData } = useUser();

  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accounts,   setAccounts]   = useState({
    usd: parseAccountStatus(null),
    gbp: parseAccountStatus(null),
    yen: parseAccountStatus(null),
  });

  /* ── fetch stripe status ── */
  const fetchStatus = useCallback(async (username, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res  = await fetch(`${API}/payment/retive-stripe-account-info/web/${username}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const { allAccountObj } = data;
      setAccounts({
        usd: parseAccountStatus(allAccountObj?.retrieveUSD),
        gbp: parseAccountStatus(allAccountObj?.retrieveGBP),
        yen: parseAccountStatus(allAccountObj?.retrieveYEN),
      });
    } catch (err) {
      if (!silent) toast.error('Could not load verification status');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  /* ── fetch and sync dashboard balance (mirrors getLoadInitalData) ── */
  const syncBalance = useCallback(async (username) => {
    try {
      const info = await fetchDashboardUser(username);
      if (info?.accountBalance) updateBalance(info.accountBalance);
    } catch { /* silent */ }
  }, [updateBalance]);

  useEffect(() => {
    const username = ctxUser?.telegramName;
    if (!username) { navigate('/login'); return; }
    // Depend only on the stable string, not the whole ctxUser object,
    // to prevent re-firing every time UserContext rebuilds the user reference.
    fetchStatus(username);
    syncBalance(username);
  }, [ctxUser?.telegramName, navigate, fetchStatus, syncBalance]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStatus(ctxUser.telegramName, true);
    await syncBalance(ctxUser.telegramName);
    if (ctxUser.pin) await refreshUserData(ctxUser.telegramName, ctxUser.pin, true);
    toast.success('Status refreshed');
    setRefreshing(false);
  };

  /* ── format balance from accountBalance string ("$1,234.00" or number) ── */
  const displayBalance = () => {
    if (!balance) return '$0.00';
    const raw = typeof balance === 'string' ? balance : `$${balance}`;
    // strip leading $ then format
    const num = parseFloat(raw.replace('$', '').replace(/,/g, ''));
    if (isNaN(num)) return raw;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  /* ─────────────────────────────────────────────────────────────────────────
     Helpers: derive completed setup steps from a live Stripe account object.
  ───────────────────────────────────────────────────────────────────────── */
  const deriveCompletedSteps = (acct, currency) => {
    if (!acct) return [];
    const completed = [];
    const ind = acct.individual || {};
    if (ind.first_name || ind.first_name_kanji) completed.push('personal');
    const addrSrc = currency === 'YEN' ? ind.address_kanji : ind.address;
    if (addrSrc?.city || addrSrc?.line1) completed.push('address');
    if (currency !== 'USD') {
      const docFront = ind?.verification?.document?.front;
      if (docFront !== null && docFront !== undefined) completed.push('upload');
    }
    if (currency === 'USD' && ind.ssn_last_4_provided) completed.push('ssn');
    if ((acct.external_accounts?.total_count || 0) > 0) completed.push('account');
    return completed;
  };

  const STEP_DETAIL = {
    personal: { label: 'Personal Info', icon: User,     desc: 'Name, DOB, phone' },
    address:  { label: 'Address',       icon: MapPin,   desc: 'City, street, postal code' },
    upload:   { label: 'Identity Docs', icon: Upload,   desc: 'Driver licence or ID card' },
    ssn:      { label: 'SSN',           icon: Lock,     desc: 'SSN last 4 & full number' },
    account:  { label: 'Bank / Card',   icon: Building, desc: 'Bank account or debit card' },
  };

  const CURRENCY_STEPS = {
    USD: ['personal', 'address', 'ssn', 'account'],
    GBP: ['personal', 'address', 'upload', 'account'],
    YEN: ['personal', 'address', 'upload', 'account'],
  };

  /* ─────────────────────────────────────────────────────────────────────────
     Account card component
  ───────────────────────────────────────────────────────────────────────── */
  const AccountCard = ({ currency, CurrIcon, parsed }) => {
    const style  = S[parsed.status] || S.NOT_CONNECTED;
    const SIcon  = style.icon;
    const acct   = parsed.accountData;
    const ind    = parsed.individual;
    const allDue = [...new Set([...parsed.reqs.pastDue, ...parsed.reqs.currentlyDue])];

    const completedSteps = acct ? deriveCompletedSteps(acct, currency) : [];
    const currencySteps  = CURRENCY_STEPS[currency] || [];
    const hasStarted     = completedSteps.length > 0;

    const goConnect = () => navigate(`/connect-account?currency=${currency}`);

    return (
      <div className={`${style.bg} border-2 ${style.border} rounded-2xl p-6 flex flex-col gap-4`}>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${style.badge} rounded-xl flex items-center justify-center shrink-0`}>
              <CurrIcon className={style.iconCls} size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{currency} Account</h3>
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${style.badge}`}>
                <SIcon size={11} /> {parsed.label}
              </span>
            </div>
          </div>
          <SIcon className={`${style.iconCls} shrink-0 mt-0.5`} size={28} />
        </div>

        {/* Account info rows */}
        {acct && (
          <div className="bg-white/70 rounded-xl p-4 space-y-2 text-sm border border-white/80">
            {ind?.first_name && (
              <div className="flex items-center gap-2 text-slate-700">
                <User size={13} className="text-slate-400 shrink-0" />
                <span className="font-medium">{ind.first_name} {ind.last_name}</span>
              </div>
            )}
            {acct.email && (
              <div className="flex items-center gap-2 text-slate-600">
                <span className="text-slate-400 shrink-0 text-xs">@</span>
                <span className="truncate">{acct.email}</span>
              </div>
            )}
            {ind?.address && (
              <div className="flex items-start gap-2 text-slate-600">
                <MapPin size={13} className="text-slate-400 shrink-0 mt-0.5" />
                <span>{[ind.address.city, ind.address.state, ind.address.country].filter(Boolean).join(', ') || 'Address incomplete'}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-slate-600">
              <CreditCard size={13} className="text-slate-400 shrink-0" />
              <span className={parsed.externalAccounts > 0 ? 'text-green-700 font-medium' : ''}>
                {parsed.externalAccounts > 0 ? `${parsed.externalAccounts} bank account linked` : 'No bank account yet'}
              </span>
            </div>
            {ind && (
              <div className="flex items-center gap-2 text-slate-600">
                <Lock size={13} className="text-slate-400 shrink-0" />
                <span className="text-xs">
                  SSN {ind.ssn_last_4_provided ? '✓' : '✗'}
                  {' · '}
                  ID {ind.id_number_provided ? '✓' : '✗'}
                  {' · '}
                  Doc: {ind.verification?.status ?? 'n/a'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Past-due / currently-due items */}
        {allDue.length > 0 && (
          <div className="rounded-xl overflow-hidden border border-red-200 text-sm">
            <div className="bg-red-100 px-4 py-2 text-xs font-bold text-red-800 uppercase tracking-wide">
              Action Required
            </div>
            <div className="bg-white divide-y divide-slate-100">
              {allDue.map((req, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 text-slate-700">
                  <XCircle size={12} className="text-red-500 shrink-0" />
                  <span className="flex-1">{friendlyReq(req)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Eventually-due (informational only, shown when nothing past-due) */}
        {parsed.reqs.eventuallyDue.length > 0 && allDue.length === 0 && (
          <div className="rounded-xl overflow-hidden border border-amber-200 text-sm">
            <div className="bg-amber-100 px-4 py-2 text-xs font-bold text-amber-800 uppercase tracking-wide">
              Will Be Needed
            </div>
            <div className="bg-white divide-y divide-slate-100">
              {parsed.reqs.eventuallyDue.map((req, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 text-slate-700">
                  <Clock size={12} className="text-amber-500 shrink-0" />
                  <span>{friendlyReq(req)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Account Details Section (only shown once user has started connecting) ── */}
        {hasStarted && (
          <div className="rounded-xl overflow-hidden border border-slate-200 text-sm">
            <div className="px-4 py-2.5 bg-slate-100 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <Edit3 size={12} /> Account Details
              </span>
              <span className="text-xs text-slate-500">Click a step to update</span>
            </div>
            <div className="bg-white divide-y divide-slate-100">
              {currencySteps.map((step, idx) => {
                const meta       = STEP_DETAIL[step];
                const Icon       = meta.icon;
                const isDone     = completedSteps.includes(step);
                const prevStep   = idx > 0 ? currencySteps[idx - 1] : null;
                const accessible = idx === 0 || (prevStep && completedSteps.includes(prevStep));

                return (
                  <button
                    key={step}
                    type="button"
                    disabled={!accessible}
                    onClick={() => accessible && navigate(`/update-account?currency=${currency}#${step}`)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                      ${accessible ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-40 cursor-not-allowed'}
                    `}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                      ${isDone ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {isDone
                        ? <CheckCircle size={15} className="text-green-600" />
                        : <Icon size={15} className="text-slate-400" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-xs">{meta.label}</p>
                      <p className="text-xs text-slate-500 truncate">{meta.desc}</p>
                    </div>
                    {accessible
                      ? <ChevronRight size={14} className="text-slate-400 shrink-0" />
                      : <Lock size={12} className="text-slate-300 shrink-0" />
                    }
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA */}
        {parsed.status === 'APPROVED' ? (
          <div className="flex items-center justify-center gap-2 py-1.5 text-green-700 font-semibold text-sm">
            <CheckCircle size={17} /> Account Active – Ready for Transactions
          </div>
        ) : (
          <button
            onClick={goConnect}
            className={`w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 transition-all shadow-sm ${style.btn}`}
          >
            {parsed.status === 'NOT_CONNECTED'
              ? <><Plus size={17} /> Connect {currency} Account</>
              : <>{style.btnLabel} <ArrowRight size={17} /></>
            }
          </button>
        )}
      </div>
    );
  };

  /* ─── loading ─── */
  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Checking account status…</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  /* ─── main ─── */
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 font-medium transition">
            <ArrowLeft size={20} /> Back to Settings
          </button>
          <button onClick={handleRefresh} disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition disabled:opacity-50">
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh Status'}
          </button>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Account Verification</h1>
          <p className="text-slate-500">
            <span className="font-semibold text-slate-700">@{ctxUser?.telegramName}</span>
          </p>
        </div>

        {/* ── Balance banner — pulled from GET /api/users/{username} ── */}
        <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 mb-8 text-center text-white overflow-hidden">
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/20 text-white text-xs font-semibold rounded-full">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Live
            </span>
          </div>
          <p className="text-sm text-blue-200 mb-1">Account Balance</p>
          {/* accountBalance from GET /api/users/{username} via UserContext */}
          <p className="text-4xl font-bold">{displayBalance()}</p>
          <p className="text-xs text-blue-300 mt-2">
            Synced via <code className="opacity-70">GET /api/users/{ctxUser?.telegramName}</code>
          </p>
        </div>

        {/* Status legend */}
        <div className="grid grid-cols-4 gap-3 mb-8">
          {[
            { icon: AlertCircle, cls: 'text-slate-500', bg: 'bg-slate-100', label: 'Not Connected' },
            { icon: XCircle,     cls: 'text-red-600',   bg: 'bg-red-100',   label: 'Restricted' },
            { icon: Clock,       cls: 'text-amber-600', bg: 'bg-amber-100', label: 'Pending' },
            { icon: CheckCircle, cls: 'text-green-600', bg: 'bg-green-100', label: 'Approved' },
          ].map(({ icon: Icon, cls, bg, label }) => (
            <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
              <Icon className={`${cls} mx-auto mb-1`} size={20} />
              <p className={`text-xs font-semibold ${cls}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Three currency cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <AccountCard currency="USD" CurrIcon={DollarSign}    parsed={accounts.usd} />
          <AccountCard currency="GBP" CurrIcon={PoundSterling} parsed={accounts.gbp} />
          <AccountCard currency="YEN" CurrIcon={Banknote}      parsed={accounts.yen} />
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <FileText size={18} /> How Verification Works
          </h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-blue-800">
            <div className="flex items-start gap-2">
              <AlertCircle size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <span><strong>Not Connected:</strong> No Stripe account yet — click Connect to start the setup wizard.</span>
            </div>
            <div className="flex items-start gap-2">
              <XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
              <span><strong>Restricted:</strong> Past-due items need resolving. The wizard will resume from where you left off.</span>
            </div>
            <div className="flex items-start gap-2">
              <Clock size={14} className="text-amber-500 mt-0.5 shrink-0" />
              <span><strong>Pending:</strong> Stripe is reviewing your submission. You can still complete any missing info.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={14} className="text-green-500 mt-0.5 shrink-0" />
              <span><strong>Approved:</strong> Fully verified — charges and payouts are enabled on your account.</span>
            </div>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
